import { FC, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import type { ComparisonResult, VlanChange, VlanAdded, PropertyChange } from '@/types/entities';

interface VlanComparisonProps {
  comparison: ComparisonResult;
}

type FilterType = 'all' | 'added' | 'removed' | 'modified';
type ChangeType = 'added' | 'removed' | 'modified';

interface VlanRow {
  type: ChangeType;
  data: VlanAdded | VlanChange;
}

export const VlanComparison: FC<VlanComparisonProps> = ({ comparison }) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { vlanChanges } = comparison;

  const toggleRow = (vlanId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(vlanId)) {
      newExpanded.delete(vlanId);
    } else {
      newExpanded.add(vlanId);
    }
    setExpandedRows(newExpanded);
  };

  const getFilteredVlans = (): VlanRow[] => {
    const { added, removed, modified } = vlanChanges;

    const rows: VlanRow[] = [];

    if (filter === 'all' || filter === 'added') {
      rows.push(...added.map(vlan => ({ type: 'added' as ChangeType, data: vlan })));
    }
    if (filter === 'all' || filter === 'removed') {
      rows.push(...removed.map(vlan => ({ type: 'removed' as ChangeType, data: vlan })));
    }
    if (filter === 'all' || filter === 'modified') {
      rows.push(...modified.map(vlan => ({ type: 'modified' as ChangeType, data: vlan })));
    }

    // Sort by VLAN ID
    rows.sort((a, b) => {
      const vlanA = a.data.vlanId;
      const vlanB = b.data.vlanId;
      return vlanA - vlanB;
    });

    return rows;
  };

  const VlanRowComponent: FC<{ row: VlanRow }> = ({ row }) => {
    const { type, data } = row;
    const isModified = type === 'modified';

    // Use the correct type based on row type
    const vlanData = data as (VlanAdded | VlanChange);
    const vlanId = vlanData.vlanId;
    const isExpanded = expandedRows.has(vlanId);

    // Show expand button for all types (to show interface lists)
    const hasInterfaceData = ('access_interfaces' in vlanData && vlanData.access_interfaces && vlanData.access_interfaces.length > 0) ||
                             ('trunk_interfaces' in vlanData && vlanData.trunk_interfaces && vlanData.trunk_interfaces.length > 0);
    const hasChanges = isModified && ((vlanData as VlanChange).changes?.length > 0 ||
                                      (vlanData as VlanChange).interfaces_added?.length > 0 ||
                                      (vlanData as VlanChange).interfaces_removed?.length > 0);
    const canExpand = hasInterfaceData || hasChanges;

    return (
      <>
        <TableRow hover>
          <TableCell width={50}>
            {canExpand && (
              <IconButton size="small" onClick={() => toggleRow(vlanId)}>
                {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              </IconButton>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="bold">
              {vlanId}
            </Typography>
          </TableCell>
          <TableCell>
            {type === 'added' && <Chip icon={<AddIcon />} label="Added" color="success" size="small" />}
            {type === 'removed' && <Chip icon={<RemoveIcon />} label="Removed" color="error" size="small" />}
            {type === 'modified' && (
              <Chip
                icon={<EditIcon />}
                label={`${(vlanData as VlanChange).changes?.length || 0} changes`}
                color="warning"
                size="small"
              />
            )}
          </TableCell>
        </TableRow>

        {/* Expanded Detail for all VLANs */}
        {(isModified || type === 'added' || type === 'removed') && (
          <TableRow>
            <TableCell colSpan={3} sx={{ py: 0 }}>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                  {/* Property Changes (for modified VLANs only) */}
                  {isModified && (vlanData as VlanChange).changes && (vlanData as VlanChange).changes.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Property Changes:
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Property</strong></TableCell>
                            <TableCell><strong>Before (A)</strong></TableCell>
                            <TableCell><strong>After (B)</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(vlanData as VlanChange).changes?.map((propChange: PropertyChange, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {propChange.propertyName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: 'line-through',
                                    color: 'error.main',
                                  }}
                                >
                                  {formatValue(propChange.beforeValue)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'success.main', fontWeight: 'bold' }}
                                >
                                  {formatValue(propChange.afterValue)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}

                  {/* Interface Membership Changes (for modified VLANs) */}
                  {isModified && (
                    <>
                      {((vlanData as VlanChange).interfaces_added?.length > 0 || (vlanData as VlanChange).interfaces_removed?.length > 0) && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Interface Membership Changes:
                          </Typography>
                          {(vlanData as VlanChange).interfaces_added && (vlanData as VlanChange).interfaces_added.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                                Added:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {(vlanData as VlanChange).interfaces_added.map((iface: string) => (
                                  <Chip
                                    key={iface}
                                    label={iface}
                                    size="small"
                                    sx={{ fontFamily: 'monospace' }}
                                    color="success"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          {(vlanData as VlanChange).interfaces_removed && (vlanData as VlanChange).interfaces_removed.length > 0 && (
                            <Box>
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                                Removed:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {(vlanData as VlanChange).interfaces_removed.map((iface: string) => (
                                  <Chip
                                    key={iface}
                                    label={iface}
                                    size="small"
                                    sx={{ fontFamily: 'monospace' }}
                                    color="error"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}

                  {/* Current Interface Membership (for all VLAN types) */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {type === 'removed' ? 'Affected Interfaces (were in this VLAN):' : 'Current Interfaces:'}
                    </Typography>
                    {('access_interfaces' in vlanData && vlanData.access_interfaces && vlanData.access_interfaces.length > 0) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Access Ports ({vlanData.access_interfaces.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {vlanData.access_interfaces.map((iface: string) => (
                            <Chip
                              key={iface}
                              label={iface}
                              size="small"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {('trunk_interfaces' in vlanData && vlanData.trunk_interfaces && vlanData.trunk_interfaces.length > 0) && (
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Trunk Ports ({vlanData.trunk_interfaces.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {vlanData.trunk_interfaces.map((iface: string) => (
                            <Chip
                              key={iface}
                              label={iface}
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {!(('access_interfaces' in vlanData && vlanData.access_interfaces && vlanData.access_interfaces.length > 0) ||
                      ('trunk_interfaces' in vlanData && vlanData.trunk_interfaces && vlanData.trunk_interfaces.length > 0)) && (
                      <Typography variant="body2" color="text.secondary">
                        No interfaces in this VLAN
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  const filteredVlans = getFilteredVlans();

  return (
    <Box>
      {/* Filter Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">VLAN Changes</Typography>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
        >
          <ToggleButton value="all">
            All ({vlanChanges.added.length + vlanChanges.removed.length + vlanChanges.modified.length})
          </ToggleButton>
          <ToggleButton value="added">
            Added ({vlanChanges.added.length})
          </ToggleButton>
          <ToggleButton value="removed">
            Removed ({vlanChanges.removed.length})
          </ToggleButton>
          <ToggleButton value="modified">
            Modified ({vlanChanges.modified.length})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Comparison Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell><strong>VLAN ID</strong></TableCell>
              <TableCell><strong>Change</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredVlans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="text.secondary">No {filter !== 'all' ? filter : ''} VLANs</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredVlans.map((row) => (
                <VlanRowComponent key={row.data.vlanId} row={row} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Statistics */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Total Changes:</strong> {filteredVlans.length} VLANs
        </Typography>
        {filter === 'all' && (
          <>
            <Typography variant="body2" color="success.main">
              +{vlanChanges.added.length}
            </Typography>
            <Typography variant="body2" color="error.main">
              -{vlanChanges.removed.length}
            </Typography>
            <Typography variant="body2" color="warning.main">
              ~{vlanChanges.modified.length}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
